import React, { useState, useEffect, useRef } from 'react';
import { doc, getDoc, setDoc, collection, onSnapshot, getDocs, query, where, deleteDoc, addDoc } from 'firebase/firestore';
import { db, appId } from '../lib/firebase';
import { calculateTotals, generateNumber, formatCurrency } from '../lib/utils';
import { PRICE_DATABASE } from '../lib/constants';
import { 
  ArrowLeft, Save, Printer, Mail, Percent, CheckSquare, GripVertical, Trash2, Plus, 
  LayoutGrid, Wand2, Loader2, Edit3, Check, PenTool, CheckCircle2, HardHat, Hammer, 
  ChevronDown, FileCheck, Cloud, Euro, Library, Search, X, UserPlus, Paperclip 
} from 'lucide-react';
import Button from './ui/Button';
import { SignaturePad, EmailModal } from './ui/Modals';

// IMPORTATION POUR LE PDF
import jsPDF from 'jspdf';
import autoTable from 'jspdf-autotable';

const Editor = ({ user, activeCompanyId, docId, type = 'quote', prospectData = null, onBack, onSaveSuccess, onRedirect, logActivity }) => {
  const [docInfo, setDocInfo] = useState({ 
    id: null, type: type, number: '', date: new Date().toLocaleDateString('fr-FR'), 
    status: 'Brouillon', signature: null, projectAddress: '', fromProspect: !!prospectData
  });
  
  const [sections, setSections] = useState([]);
  const [client, setClient] = useState({});
  const [clientsList, setClientsList] = useState([]); 
  
  const [globalSettings, setGlobalSettings] = useState({ 
      downPaymentValue: 30, downPaymentType: 'percent', discountValue: 0, discountType: 'percent' 
  });
  
  const [companyInfo, setCompanyInfo] = useState({
      name: 'BatiFlow', address: 'Adresse', city: 'Ville', email: 'contact@batiflow.net', phone: '', logoUrl: null, defaultVat: 20, cgv: ''
  });

  const [libraryItems, setLibraryItems] = useState([]);
  const [showLibraryModal, setShowLibraryModal] = useState(false);
  const [targetSectionIndex, setTargetSectionIndex] = useState(null); 
  const [librarySearch, setLibrarySearch] = useState('');
  const [isEditingAddress, setIsEditingAddress] = useState(false);
  const [showSignatureModal, setShowSignatureModal] = useState(false);
  const [showEmailModal, setShowEmailModal] = useState(false);
  const [showClientSelector, setShowClientSelector] = useState(false);
  const [showNewClientModal, setShowNewClientModal] = useState(false);
  const [showEditClientModal, setShowEditClientModal] = useState(false);
  const [newClientForm, setNewClientForm] = useState({ name: '', email: '', phone: '', phoneMobile: '', address: '', city: '', siret: '', contactName: '', contactFunction: '', notes: '' });
  const [editClientForm, setEditClientForm] = useState({ name: '', email: '', phone: '', phoneMobile: '', address: '', city: '', siret: '', contactName: '', contactFunction: '', notes: '' });
  const [lastAutoSave, setLastAutoSave] = useState(null);
  const [isSaving, setIsSaving] = useState(false);
  const [draggedItem, setDraggedItem] = useState(null);
  const [isReadOnly, setIsReadOnly] = useState(!!docId); // Mode lecture seule si on ouvre un document existant
  const [draggedSectionIndex, setDraggedSectionIndex] = useState(null);

  const dataRef = useRef({ docInfo, sections, client, globalSettings });
  const prospectInitializedRef = useRef(false);
  const documentCreatedRef = useRef(false);
  useEffect(() => { dataRef.current = { docInfo, sections, client, globalSettings }; }, [docInfo, sections, client, globalSettings]);

  useEffect(() => {
    if (!user || !activeCompanyId) return;
    const unsubClients = onSnapshot(collection(db, 'companies', activeCompanyId, 'clients'), (snap) => setClientsList(snap.docs.map(d => ({id: d.id, ...d.data()}))));
    const unsubLibrary = onSnapshot(collection(db, 'artifacts', appId, 'users', user.uid, 'library'), (snap) => setLibraryItems(snap.docs.map(d => ({id: d.id, ...d.data()}))));
    
    const loadSettingsAndDoc = async () => {
        let prefVat = 20; let prefDownPayment = 30; let prefDownType = 'percent';
        try {
            const sSnap = await getDoc(doc(db, 'artifacts', appId, 'users', user.uid, 'settings', 'general'));
            if(sSnap.exists()) {
                const sData = sSnap.data();
                if(sData.company) {
                    setCompanyInfo(prev => ({ ...prev, ...sData.company, defaultVat: Number(sData.company.defaultVat) || 20 }));
                    prefVat = Number(sData.company.defaultVat) || 20;
                    if (sData.company.defaultDownPaymentValue) prefDownPayment = Number(sData.company.defaultDownPaymentValue);
                    if (sData.company.defaultDownPaymentType) prefDownType = sData.company.defaultDownPaymentType;
                }
            }
        } catch(e) { console.error(e); }

        if (docId) {
            const collectionName = type === 'quote' ? 'quotes' : 'invoices';
            const snap = await getDoc(doc(db, 'companies', activeCompanyId, collectionName, String(docId)));
            if (snap.exists()) {
                const data = snap.data();
                setDocInfo({ id: data.id, type: data.type, number: data.number, date: data.date, status: data.status, signature: data.signature, projectAddress: data.projectAddress || '' });
                setSections(data.sections || []);
                
                // Charger le client complet depuis la collection clients si on a un clientId
                if (data.clientId) {
                    try {
                        const clientSnap = await getDoc(doc(db, 'companies', activeCompanyId, 'clients', String(data.clientId)));
                        if (clientSnap.exists()) {
                            setClient({ id: clientSnap.id, ...clientSnap.data() });
                        } else {
                            setClient({ name: data.clientName || '' });
                        }
                    } catch (e) {
                        console.error("Erreur chargement client:", e);
                        setClient({ name: data.clientName || '' });
                    }
                } else {
                    // Fallback: chercher par nom dans la liste
                    setClient({ name: data.clientName || '' });
                }
            }
        } else {
            // Protection contre la double création
            if (documentCreatedRef.current) {
                return;
            }
            documentCreatedRef.current = true;
            
            const newId = Date.now();
            const newNum = generateNumber(type);
            
            // Pré-remplir avec les données du prospect ou du bien si disponibles
            if (prospectData && !prospectInitializedRef.current) {
                prospectInitializedRef.current = true;
                
                // Détecter si c'est un bien (propertyData) ou un prospect
                const isProperty = prospectData.propertyId && prospectData.propertyAddress;
                
                if (isProperty) {
                    // Données venant d'un bien
                    setClient({
                        name: prospectData.clientName || '',
                        email: '',
                        phone: '',
                        address: ''
                    });
                    setDocInfo({ 
                        id: newId, 
                        type: prospectData.type || type, 
                        number: newNum, 
                        date: new Date().toLocaleDateString('fr-FR'), 
                        status: 'Brouillon', 
                        signature: null, 
                        projectAddress: `${prospectData.propertyAddress}, ${prospectData.propertyPostalCode} ${prospectData.propertyCity}`, 
                        clientName: prospectData.clientName || '', 
                        total: 0,
                        fromProperty: true,
                        propertyId: prospectData.propertyId,
                        clientId: prospectData.clientId
                    });
                } else {
                    // Données venant d'un prospect
                    setClient({
                        name: prospectData.clientName || '',
                        email: prospectData.clientEmail || '',
                        phone: prospectData.clientPhone || '',
                        address: prospectData.clientAddress || ''
                    });
                    setDocInfo({ 
                        id: newId, 
                        type: type, 
                        number: newNum, 
                        date: new Date().toLocaleDateString('fr-FR'), 
                        status: 'Brouillon', 
                        signature: null, 
                        projectAddress: prospectData.clientAddress || '', 
                        clientName: prospectData.clientName || '', 
                        total: 0,
                        fromProspect: true,
                        prospectId: prospectData.prospectId
                    });
                }
                
                // Créer la première section avec description détaillée + liste de tâches
                const initialItems = [];
                
                if (isProperty) {
                    // Pour un bien, ajouter les notes du bien si elles existent
                    if (prospectData.notes) {
                        initialItems.push({
                            id: Date.now(),
                            desc: `📝 NOTES SUR LE BIEN :\n\n${prospectData.notes}`,
                            qty: 0,
                            unit: 'forfait',
                            price: 0,
                            vat: prefVat,
                            nature: null,
                            isNote: true  // Marquer comme note pour la cacher
                        });
                    }
                } else {
                    // Pour un prospect, utiliser la logique existante
                    console.log('📝 Tâches du prospect:', prospectData.tasks);
                    
                    // Ajouter la description détaillée comme premier item si elle existe (caché)
                    if (prospectData.detailedDescription || prospectData.notes) {
                        let notesText = '📝 NOTES DU RDV CLIENT :\n\n';
                        if (prospectData.detailedDescription) {
                            notesText += prospectData.detailedDescription;
                        }
                        if (prospectData.notes) {
                            notesText += '\n\n💡 Informations complémentaires :\n' + prospectData.notes;
                        }
                        
                        initialItems.push({
                            id: Date.now(),
                            desc: notesText,
                            qty: 0,
                            unit: 'forfait',
                            price: 0,
                            vat: prefVat,
                            nature: null,
                            isNote: true  // Marquer comme note pour la cacher
                        });
                    }
                    
                    // Ajouter les tâches comme items
                    if (prospectData.tasks && prospectData.tasks.length > 0) {
                        prospectData.tasks.forEach((task, idx) => {
                            initialItems.push({
                                id: Date.now() + idx + 1,
                                desc: task.title,
                                qty: 1,
                                unit: 'unité',
                                price: 0,
                                vat: prefVat,
                                nature: null
                            });
                        });
                    }
                }
                
                if (initialItems.length > 0) {
                    const initialSection = {
                        id: Date.now(),
                        title: isProperty ? 'Travaux' : 'Travaux demandés par le client',
                        items: initialItems
                    };
                    const initialSections = [initialSection];
                    setSections(initialSections);
                }
            }
            
            // Créer le document dans Firestore (avec ou sans prospect)
            // Utiliser la signature de l'entreprise si elle existe dans les paramètres
            const companySignature = companyInfo.signatureUrl || null;
            
            setDocInfo({ 
                id: newId, 
                type: type, 
                number: newNum, 
                date: new Date().toLocaleDateString('fr-FR'), 
                status: 'Brouillon', 
                signature: companySignature, 
                projectAddress: '', 
                fromProspect: prospectData ? true : undefined 
            });
            setGlobalSettings({ downPaymentValue: prefDownPayment, downPaymentType: prefDownType, discountValue: 0, discountType: 'percent' });
            
            console.log('💾 Sauvegarde document dans Firestore...');
            const initialData = { 
                id: newId, 
                type, 
                companyId: activeCompanyId, 
                number: newNum, 
                date: new Date().toLocaleDateString('fr-FR'), 
                status: 'Brouillon',
                signature: companySignature,
                clientName: prospectData?.clientName || ''
            };
            if (prospectData) {
                initialData.fromProspect = true;
            }
            await setDoc(doc(db, 'companies', activeCompanyId, type === 'quote' ? 'quotes' : 'invoices', String(newId)), initialData);
        }
    };
    loadSettingsAndDoc();

    const autoSaveInterval = setInterval(async () => {
        const currentData = dataRef.current;
        if (!user || !currentData.docInfo.id) return;
        try {
            setIsSaving(true);
            const totals = calculateTotals(currentData.sections, currentData.globalSettings);
            const collectionName = type === 'quote' ? 'quotes' : 'invoices';
            const dataToSave = Object.fromEntries(
                Object.entries({ ...currentData.docInfo, clientName: currentData.client.name || '', clientId: currentData.client.id || null, total: totals.totalTTC, sections: currentData.sections })
                    .filter(([_, value]) => value !== undefined)
            );
            await setDoc(doc(db, 'companies', activeCompanyId, collectionName, String(currentData.docInfo.id)), dataToSave);
            setLastAutoSave(new Date()); setIsSaving(false);
        } catch (error) { setIsSaving(false); }
    }, 10000);

    return () => { unsubClients(); unsubLibrary(); clearInterval(autoSaveInterval); };
  }, [user, docId, type]);

  // Effet pour synchroniser le client avec la liste des clients chargée
  useEffect(() => {
    if (!client.name || client.email) return; // Si on a déjà les coordonnées complètes, pas besoin
    if (clientsList.length === 0) return; // Si la liste n'est pas encore chargée
    
    // Chercher le client dans la liste par son nom
    const foundClient = clientsList.find(c => c.name === client.name);
    if (foundClient && foundClient.email) {
      setClient(foundClient);
    }
  }, [clientsList, client.name]);

  const totals = calculateTotals(sections, globalSettings);

  // --- GÉNÉRATION DU PDF ---
  const generatePDF = () => {
    const doc = new jsPDF({ unit: 'pt' });
    const pageWidth = doc.internal.pageSize.getWidth();
    const margin = 40;
    let cursorY = margin;

    // Bande colorée en haut (header moderne)
    doc.setFillColor(37, 99, 235); // Bleu moderne
    doc.rect(0, 0, pageWidth, 8, 'F');

    cursorY += 20;

    // Header: logo + company info (layout horizontal amélioré)
    if (companyInfo.logoUrl) {
      try { 
        doc.addImage(companyInfo.logoUrl, 'PNG', margin, cursorY, 100, 50); 
      } catch (e) { 
        try { 
          doc.addImage(companyInfo.logoUrl, 'JPEG', margin, cursorY, 100, 50); 
        } catch (e2) { /* ignore */ } 
      }
    }

    const companyX = margin + (companyInfo.logoUrl ? 115 : 0);
    doc.setFontSize(16);
    doc.setFont('helvetica', 'bold');
    doc.setTextColor(30, 30, 30);
    doc.text(companyInfo.name || '', companyX, cursorY + 14);
    
    doc.setFontSize(9);
    doc.setFont('helvetica', 'normal');
    doc.setTextColor(100, 100, 100);
    const infoLines = [companyInfo.address, companyInfo.city, companyInfo.phone, companyInfo.email].filter(Boolean);
    infoLines.forEach((l, i) => doc.text(l, companyX, cursorY + 32 + i * 12));

    // Client box moderne (design carte)
    const clientBoxWidth = 220;
    const clientBoxX = pageWidth - margin - clientBoxWidth;
    
    // Ombre subtile
    doc.setFillColor(0, 0, 0);
    doc.setGState(new doc.GState({opacity: 0.05}));
    doc.roundedRect(clientBoxX + 2, cursorY + 2, clientBoxWidth, 75, 8, 8, 'F');
    doc.setGState(new doc.GState({opacity: 1}));
    
    // Box client avec bordure
    doc.setFillColor(248, 250, 252);
    doc.setDrawColor(226, 232, 240);
    doc.setLineWidth(1);
    doc.roundedRect(clientBoxX, cursorY, clientBoxWidth, 75, 8, 8, 'FD');
    
    doc.setFontSize(8);
    doc.setFont('helvetica', 'bold');
    doc.setTextColor(100, 116, 139);
    doc.text('CLIENT', clientBoxX + 14, cursorY + 16);
    
    doc.setFont('helvetica', 'bold');
    doc.setFontSize(11);
    doc.setTextColor(30, 30, 30);
    doc.text(client.name || '', clientBoxX + 14, cursorY + 32);
    
    doc.setFont('helvetica', 'normal');
    doc.setFontSize(9);
    doc.setTextColor(100, 100, 100);
    const clientLines = [client.address || '', client.city || ''].filter(Boolean);
    clientLines.forEach((l, i) => doc.text(l, clientBoxX + 14, cursorY + 46 + i * 12));

    cursorY += 100;

    // Ligne de séparation élégante
    doc.setDrawColor(226, 232, 240);
    doc.setLineWidth(0.5);
    doc.line(margin, cursorY, pageWidth - margin, cursorY);
    
    cursorY += 25;

    // Title moderne avec badge de couleur
    const titleText = type === 'quote' ? 'DEVIS' : 'FACTURE';
    doc.setFillColor(37, 99, 235);
    doc.roundedRect(margin, cursorY - 8, 100, 26, 4, 4, 'F');
    doc.setFontSize(14);
    doc.setFont('helvetica', 'bold');
    doc.setTextColor(255, 255, 255);
    doc.text(titleText, margin + 50, cursorY + 8, { align: 'center' });
    
    doc.setFontSize(18);
    doc.setFont('helvetica', 'bold');
    doc.setTextColor(30, 30, 30);
    doc.text(`N° ${docInfo.number}`, margin + 110, cursorY + 8);
    
    doc.setFontSize(9);
    doc.setFont('helvetica', 'normal');
    doc.setTextColor(100, 100, 100);
    doc.text(`Date : ${docInfo.date}`, pageWidth - margin, cursorY + 8, { align: 'right' });
    
    // Date de validité du devis
    if (type === 'quote' && companyInfo.quoteValidityDays) {
      const validityDate = new Date(docInfo.date);
      validityDate.setDate(validityDate.getDate() + (companyInfo.quoteValidityDays || 30));
      doc.setFont('helvetica', 'italic');
      doc.setTextColor(71, 85, 105);
      doc.text(`Valable jusqu'au : ${validityDate.toLocaleDateString('fr-FR')}`, pageWidth - margin, cursorY + 22, { align: 'right' });
      doc.setFont('helvetica', 'normal');
      doc.setTextColor(100, 100, 100);
    }
    
    if (docInfo.projectAddress) {
      doc.text(`Chantier : ${docInfo.projectAddress}`, pageWidth - margin, cursorY + (type === 'quote' && companyInfo.quoteValidityDays ? 36 : 22), { align: 'right' });
    }
    
    // Délai d'exécution si renseigné
    if (companyInfo.defaultExecutionDelay) {
      const execY = cursorY + (docInfo.projectAddress ? 50 : 36);
      doc.setFontSize(8);
      doc.setFont('helvetica', 'italic');
      doc.setTextColor(100, 116, 139);
      doc.text(`Délai d'exécution : ${companyInfo.defaultExecutionDelay}`, pageWidth - margin, execY, { align: 'right' });
    }

    cursorY += 40;

    // Helper to format numbers for PDF in French style (no NBSP, use regular space)
    const pdfFormat = (value, withSymbol = true) => {
      try {
        const num = Number(value) || 0;
        const parts = num.toFixed(2).split('.');
        let intPart = parts[0];
        // insert regular spaces every 3 digits
        intPart = intPart.replace(/\B(?=(\d{3})+(?!\d))/g, ' ');
        const formatted = `${intPart},${parts[1]}`;
        return withSymbol ? `${formatted} €` : formatted;
      } catch (e) { return String(value); }
    };

    // Table body
    const tableBody = [];
    sections.forEach(section => {
      tableBody.push([{ content: section.title.toUpperCase(), colSpan: 5, styles: { fillColor: [240,240,240], fontStyle: 'bold' } }]);
      section.items.forEach(item => {
        tableBody.push([
          { content: String(item.desc || ''), styles: { cellPadding: 4 } },
          { content: String(item.qty || ''), styles: { halign: 'center' } },
          { content: String(item.unit || ''), styles: { halign: 'center' } },
          { content: pdfFormat(item.price).replace('€','').trim(), styles: { halign: 'right' } },
          { content: pdfFormat((item.qty || 0) * (item.price || 0)).replace('€','').trim(), styles: { halign: 'right' } }
        ]);
      });
    });

    autoTable(doc, {
      startY: cursorY,
      head: [['Désignation', 'Qté', 'Unité', 'PU HT', 'Total HT']],
      body: tableBody,
      theme: 'striped',
      styles: { 
        fontSize: 9, 
        cellPadding: 8, 
        overflow: 'linebreak',
        lineColor: [226, 232, 240],
        lineWidth: 0.5
      },
      headStyles: { 
        fillColor: [51, 65, 85],
        textColor: 255,
        fontSize: 9,
        fontStyle: 'bold',
        halign: 'left'
      },
      alternateRowStyles: {
        fillColor: [248, 250, 252]
      },
      columnStyles: { 
        0: { cellWidth: 'auto' }, 
        1: { cellWidth: 45, halign: 'center' }, 
        2: { cellWidth: 45, halign: 'center' }, 
        3: { cellWidth: 80, halign: 'right' }, 
        4: { cellWidth: 80, halign: 'right', fontStyle: 'bold' } 
      },
      didDrawPage: (data) => {},
    });

    const finalY = (doc.lastAutoTable && doc.lastAutoTable.finalY) ? doc.lastAutoTable.finalY + 20 : cursorY + 20;

    // Totals box moderne avec dégradé visuel
    const totalsBoxWidth = 240;
    const totalsX = pageWidth - margin - totalsBoxWidth;
    
    // Ombre du box
    doc.setFillColor(0, 0, 0);
    doc.setGState(new doc.GState({opacity: 0.08}));
    doc.roundedRect(totalsX + 3, finalY + 3, totalsBoxWidth, 85, 8, 8, 'F');
    doc.setGState(new doc.GState({opacity: 1}));
    
    // Box principal
    doc.setFillColor(248, 250, 252);
    doc.setDrawColor(226, 232, 240);
    doc.setLineWidth(1);
    doc.roundedRect(totalsX, finalY, totalsBoxWidth, 85, 8, 8, 'FD');
    
    doc.setFontSize(10);
    doc.setFont('helvetica', 'normal');
    doc.setTextColor(71, 85, 105);
    doc.text('Total HT', totalsX + 16, finalY + 20);
    doc.setTextColor(30, 30, 30);
    doc.text(pdfFormat(totals.rawTotalHT), totalsX + totalsBoxWidth - 16, finalY + 20, { align: 'right' });
    
    doc.setTextColor(71, 85, 105);
    doc.text(`TVA (${companyInfo.defaultVat}%)`, totalsX + 16, finalY + 40);
    doc.setTextColor(30, 30, 30);
    doc.text(pdfFormat(totals.totalTVA), totalsX + totalsBoxWidth - 16, finalY + 40, { align: 'right' });
    
    // Ligne de séparation avant total
    doc.setDrawColor(203, 213, 225);
    doc.setLineWidth(1);
    doc.line(totalsX + 16, finalY + 52, totalsX + totalsBoxWidth - 16, finalY + 52);
    
    // Total TTC avec fond bleu
    doc.setFillColor(37, 99, 235);
    doc.roundedRect(totalsX + 10, finalY + 60, totalsBoxWidth - 20, 18, 4, 4, 'F');
    
    doc.setFont('helvetica', 'bold');
    doc.setFontSize(11);
    doc.setTextColor(255, 255, 255);
    doc.text('NET À PAYER', totalsX + 16, finalY + 72);
    doc.setFontSize(13);
    doc.text(pdfFormat(totals.totalTTC), totalsX + totalsBoxWidth - 16, finalY + 72, { align: 'right' });

    // Signature area moderne: client + entreprise côte à côte
    const sigY = finalY + 110;
    const sigWidth = 230;
    const sigHeight = 75;
    const gap = 35;
    const leftX = margin;
    const rightX = margin + sigWidth + gap;

    // Client signature box moderne
    doc.setFontSize(9);
    doc.setFont('helvetica', 'bold');
    doc.setTextColor(71, 85, 105);
    doc.text('SIGNATURE CLIENT', leftX, sigY - 10);
    
    // Mentions légales au-dessus de la signature
    doc.setFontSize(7);
    doc.setFont('helvetica', 'italic');
    doc.setTextColor(100, 116, 139);
    doc.text('Bon pour accord - Lu et approuvé', leftX, sigY - 2);
    
    doc.setDrawColor(203, 213, 225);
    doc.setLineWidth(1.5);
    doc.setFillColor(255, 255, 255);
    doc.roundedRect(leftX, sigY, sigWidth, sigHeight, 6, 6, 'FD');
    
    if (docInfo.signature) {
      try { 
        doc.addImage(docInfo.signature, 'PNG', leftX + 5, sigY + 5, sigWidth - 10, sigHeight - 10); 
      } catch (e) { 
        try { 
          doc.addImage(docInfo.signature, 'JPEG', leftX + 5, sigY + 5, sigWidth - 10, sigHeight - 10); 
        } catch (e2) { 
          doc.setFontSize(9);
          doc.setFont('helvetica', 'italic');
          doc.setTextColor(148, 163, 184);
          doc.text('En attente de signature', leftX + sigWidth/2, sigY + sigHeight/2, { align: 'center' });
        } 
      }
    } else {
      doc.setFontSize(9);
      doc.setFont('helvetica', 'italic');
      doc.setTextColor(148, 163, 184);
      doc.text('En attente de signature', leftX + sigWidth/2, sigY + sigHeight/2, { align: 'center' });
    }

    // Company signature box moderne
    doc.setFontSize(9);
    doc.setFont('helvetica', 'bold');
    doc.setTextColor(71, 85, 105);
    doc.text('SIGNATURE ENTREPRISE', rightX, sigY - 10);
    
    doc.setDrawColor(203, 213, 225);
    doc.setLineWidth(1.5);
    doc.setFillColor(255, 255, 255);
    doc.roundedRect(rightX, sigY, sigWidth, sigHeight, 6, 6, 'FD');
    
    const companySig = companyInfo.signatureUrl || companyInfo.signature || companyInfo.companySignature;
    if (companySig) {
      try { 
        doc.addImage(companySig, 'PNG', rightX + 5, sigY + 5, sigWidth - 10, sigHeight - 10); 
      } catch (e) { 
        try { 
          doc.addImage(companySig, 'JPEG', rightX + 5, sigY + 5, sigWidth - 10, sigHeight - 10); 
        } catch (e2) { 
          doc.setFontSize(9);
          doc.setFont('helvetica', 'italic');
          doc.setTextColor(148, 163, 184);
          doc.text('En attente de signature', rightX + sigWidth/2, sigY + sigHeight/2, { align: 'center' });
        } 
      }
    } else {
      doc.setFontSize(9);
      doc.setFont('helvetica', 'italic');
      doc.setTextColor(148, 163, 184);
      doc.text('En attente de signature', rightX + sigWidth/2, sigY + sigHeight/2, { align: 'center' });
    }

    // Informations de paiement (RIB + méthodes)
    let paymentY = sigY + sigHeight + 20;
    
    // Section titre "Conditions de paiement"
    doc.setFillColor(241, 245, 249);
    doc.rect(margin, paymentY, pageWidth - margin * 2, 12, 'F');
    doc.setFontSize(10);
    doc.setFont('helvetica', 'bold');
    doc.setTextColor(51, 65, 85);
    doc.text('CONDITIONS DE PAIEMENT', margin + 5, paymentY + 8);
    paymentY += 20;

    // RIB si disponible
    if (companyInfo.iban) {
      doc.setFontSize(9);
      doc.setFont('helvetica', 'bold');
      doc.setTextColor(71, 85, 105);
      doc.text('Coordonnées bancaires :', margin, paymentY);
      paymentY += 12;
      
      doc.setFont('helvetica', 'normal');
      doc.setTextColor(100, 116, 139);
      if (companyInfo.bankName) {
        doc.text(`Banque : ${companyInfo.bankName}`, margin + 5, paymentY);
        paymentY += 10;
      }
      doc.text(`IBAN : ${companyInfo.iban}`, margin + 5, paymentY);
      paymentY += 10;
      if (companyInfo.bic) {
        doc.text(`BIC : ${companyInfo.bic}`, margin + 5, paymentY);
        paymentY += 10;
      }
      paymentY += 8;
    }

    // Méthodes de paiement acceptées
    if (companyInfo.paymentMethods) {
      const methods = [];
      if (companyInfo.paymentMethods.virement) methods.push('Virement bancaire');
      if (companyInfo.paymentMethods.cheque) methods.push('Chèque');
      if (companyInfo.paymentMethods.especes) methods.push('Espèces');
      if (companyInfo.paymentMethods.cb) methods.push('Carte bancaire');
      
      if (methods.length > 0) {
        doc.setFontSize(9);
        doc.setFont('helvetica', 'bold');
        doc.setTextColor(71, 85, 105);
        doc.text('Modes de paiement acceptés :', margin, paymentY);
        paymentY += 12;
        
        doc.setFont('helvetica', 'normal');
        doc.setTextColor(100, 116, 139);
        doc.text(methods.join(' • '), margin + 5, paymentY);
        paymentY += 15;
      }
    }

    // Délai de paiement et pénalités de retard (mentions légales obligatoires)
    if (companyInfo.paymentTermDays || companyInfo.latePenaltyText) {
      doc.setFontSize(8);
      doc.setFont('helvetica', 'bold');
      doc.setTextColor(51, 65, 85);
      doc.text('Conditions de règlement :', margin, paymentY);
      paymentY += 10;
      
      doc.setFont('helvetica', 'normal');
      doc.setFontSize(7);
      doc.setTextColor(71, 85, 105);
      
      if (companyInfo.paymentTermDays) {
        doc.text(`• Paiement à ${companyInfo.paymentTermDays} jours`, margin + 5, paymentY);
        paymentY += 9;
      }
      
      if (companyInfo.latePenaltyText) {
        const penaltyLines = doc.splitTextToSize(companyInfo.latePenaltyText, pageWidth - margin * 2 - 10);
        penaltyLines.forEach((line, idx) => {
          doc.text(idx === 0 ? `• ${line}` : `  ${line}`, margin + 5, paymentY);
          paymentY += 9;
        });
      }
      
      paymentY += 10;
    }

    // Échéancier de paiement si acompte
    if (docInfo.downPayment && docInfo.downPayment > 0) {
      const remaining = totals.totalTTC - docInfo.downPayment;
      doc.setFontSize(9);
      doc.setFont('helvetica', 'bold');
      doc.setTextColor(71, 85, 105);
      doc.text('Échéancier de paiement :', margin, paymentY);
      paymentY += 12;
      
      doc.setFontSize(8);
      doc.setFont('helvetica', 'normal');
      doc.setTextColor(100, 116, 139);
      doc.text(`→ Acompte à la commande : ${docInfo.downPayment.toFixed(2)} €`, margin + 5, paymentY);
      paymentY += 10;
      doc.text(`→ Solde à la livraison : ${remaining.toFixed(2)} €`, margin + 5, paymentY);
      paymentY += 15;
    }

    // Assurance décennale si renseignée
    if (companyInfo.insuranceName && companyInfo.insurancePolicy) {
      doc.setFontSize(7);
      doc.setFont('helvetica', 'italic');
      doc.setTextColor(100, 116, 139);
      doc.text(`Assurance : ${companyInfo.insuranceName} - Police N° ${companyInfo.insurancePolicy} - Zone : ${companyInfo.insuranceArea || 'France'}`, margin, paymentY);
      paymentY += 10;
    }

    // Legal footer moderne avec bordure haute
    const footerY = doc.internal.pageSize.getHeight() - 60;
    doc.setDrawColor(226, 232, 240);
    doc.setLineWidth(0.5);
    doc.line(margin, footerY, pageWidth - margin, footerY);
    
    if (companyInfo.legalFooter) {
      doc.setFontSize(7);
      doc.setFont('helvetica', 'normal');
      doc.setTextColor(120, 120, 120);
      const footerText = doc.splitTextToSize(companyInfo.legalFooter, pageWidth - margin * 2);
      doc.text(footerText, margin, footerY + 12);
    }
    
    // Bande colorée en bas
    doc.setFillColor(37, 99, 235);
    doc.rect(0, doc.internal.pageSize.getHeight() - 8, pageWidth, 8, 'F');

    // Ajouter les CGV sur une 2ème page si disponibles
    if (companyInfo.cgv && companyInfo.cgv.trim()) {
      doc.addPage();
      
      // Header de la page CGV
      doc.setFillColor(241, 245, 249);
      doc.rect(0, 0, pageWidth, 50, 'F');
      doc.setFontSize(18);
      doc.setFont('helvetica', 'bold');
      doc.setTextColor(15, 23, 42);
      doc.text('Conditions Générales de Vente', pageWidth / 2, 30, { align: 'center' });
      
      // Contenu des CGV
      let cgvY = 60;
      doc.setFontSize(9);
      doc.setFont('helvetica', 'normal');
      doc.setTextColor(51, 65, 85);
      
      const cgvLines = companyInfo.cgv.split('\n');
      cgvLines.forEach(line => {
        if (cgvY > doc.internal.pageSize.getHeight() - 30) {
          doc.addPage();
          cgvY = 20;
        }
        const wrappedLines = doc.splitTextToSize(line || ' ', pageWidth - margin * 2);
        doc.text(wrappedLines, margin, cgvY);
        cgvY += wrappedLines.length * 12;
      });
      
      // Bande colorée en bas de la page CGV
      doc.setFillColor(37, 99, 235);
      doc.rect(0, doc.internal.pageSize.getHeight() - 8, pageWidth, 8, 'F');
    }

    // Numérotation des pages (sur toutes les pages)
    const totalPages = doc.internal.getNumberOfPages();
    for (let i = 1; i <= totalPages; i++) {
      doc.setPage(i);
      doc.setFontSize(8);
      doc.setFont('helvetica', 'normal');
      doc.setTextColor(150, 150, 150);
      doc.text(`Page ${i}/${totalPages}`, pageWidth / 2, doc.internal.pageSize.getHeight() - 15, { align: 'center' });
    }

    const pdfOutput = doc.output('datauristring');
    return pdfOutput.split(',')[1];
  };           

  const handleRequestSignature = async () => {
    if (!client.email) {
      alert("Ce client n'a pas d'adresse email pour recevoir la demande de signature.");
      return;
    }

    if (!docInfo.id || !docInfo.number) {
      alert("Le document doit être sauvegardé avec un numéro avant de demander une signature.");
      return;
    }

    const confirmed = window.confirm(
      `Envoyer une demande de signature électronique à ${client.name} (${client.email}) ?\n\n` +
      `Un lien sécurisé sera envoyé par email avec un code de vérification.`
    );

    if (!confirmed) return;

    try {
      // Importer getFunctions et httpsCallable
      const { getFunctions, httpsCallable } = await import('firebase/functions');
      const functions = getFunctions();
      const createSignatureRequest = httpsCallable(functions, 'createSignatureRequest');

      const result = await createSignatureRequest({
        companyId: activeCompanyId,
        documentId: String(docInfo.id),
        documentType: type,
        clientEmail: client.email,
        clientName: client.name || 'Client',
        documentNumber: docInfo.number
      });

      if (result.data.success) {
        alert(`✅ Demande de signature envoyée à ${client.email} !\n\nLe client recevra un lien sécurisé pour signer le document.`);
        
        if (logActivity) {
          logActivity('request_signature', {
            documentType: type,
            documentNumber: docInfo.number,
            clientEmail: client.email
          });
        }
      }
    } catch (error) {
      console.error('Erreur demande signature:', error);
      alert('Erreur lors de l\'envoi de la demande de signature. Veuillez réessayer.');
    }
  };

  const handleEmailSent = async (emailData) => {
      setShowEmailModal(false);
      if (!client.email) { alert("Ce client n'a pas d'adresse email."); return; }
      try {
          // Créer une demande de signature pour les devis
          let signatureUrl = '';
          if (type === 'quote' && docInfo.id && !docInfo.signed) {
            try {
              const { getFunctions, httpsCallable } = await import('firebase/functions');
              const functions = getFunctions();
              const createSignatureRequest = httpsCallable(functions, 'createSignatureRequest');

              const result = await createSignatureRequest({
                companyId: activeCompanyId,
                documentId: String(docInfo.id),
                documentType: type,
                clientEmail: client.email,
                clientName: client.name || 'Client',
                documentNumber: docInfo.number,
                sendEmail: false // Ne pas envoyer d'email, on l'envoie nous-mêmes avec le PDF
              });

              if (result.data.success) {
                signatureUrl = result.data.signatureUrl;
              }
            } catch (err) {
              console.error('Erreur création lien signature:', err);
              // Continuer sans le lien de signature si erreur
            }
          }

          const pdfBase64 = generatePDF();
          const pdfName = `${type === 'quote' ? 'Devis' : 'Facture'}_${docInfo.number}.pdf`;
          const attachments = [{ filename: pdfName, content: pdfBase64, encoding: 'base64' }];
          if (emailData.attachment) { attachments.push({ filename: emailData.attachment.filename, content: emailData.attachment.content, encoding: 'base64' }); }

          // Bouton de signature pour les devis
          let signatureButtonHTML = '';
          if (type === 'quote' && signatureUrl) {
            signatureButtonHTML = `
              <div style="margin: 30px 0; padding: 25px; background: linear-gradient(135deg, #fef3c7 0%, #fde68a 100%); border-radius: 12px; text-align: center; border: 2px solid #fbbf24;">
                <h3 style="margin: 0 0 10px 0; color: #92400e; font-size: 18px;">✍️ Signature électronique</h3>
                <p style="margin: 0 0 20px 0; color: #78350f; font-size: 14px;">
                  Pour accepter ce devis, signez-le directement en ligne
                </p>
                <a href="${signatureUrl}" 
                   style="background: linear-gradient(135deg, #f97316 0%, #ea580c 100%); 
                          color: white; 
                          padding: 16px 40px; 
                          text-decoration: none; 
                          border-radius: 8px; 
                          display: inline-block;
                          font-weight: bold;
                          font-size: 16px;
                          box-shadow: 0 4px 6px rgba(249, 115, 22, 0.4);">
                  ✍️ SIGNER LE DEVIS
                </a>
                <p style="margin: 15px 0 0 0; color: #78350f; font-size: 12px;">
                  🔒 Signature sécurisée avec code de vérification par email
                </p>
              </div>
            `;
          }

          // Construire le HTML des conditions de paiement
          let paymentInfoHTML = '';
          if (companyInfo.iban || companyInfo.paymentTermDays || companyInfo.latePenaltyText) {
            paymentInfoHTML = '<div style="margin-top: 30px; padding: 20px; background-color: #f8fafc; border-left: 4px solid #3b82f6; border-radius: 8px;">';
            paymentInfoHTML += '<h3 style="margin: 0 0 15px 0; color: #1e293b; font-size: 16px; font-weight: bold;">💳 Conditions de paiement</h3>';
            
            // RIB
            if (companyInfo.iban) {
              paymentInfoHTML += '<div style="margin-bottom: 15px; padding: 15px; background: white; border-radius: 6px; border: 1px solid #e2e8f0;">';
              paymentInfoHTML += '<div style="font-weight: bold; color: #475569; margin-bottom: 8px;">🏦 Coordonnées bancaires :</div>';
              if (companyInfo.bankName) {
                paymentInfoHTML += `<div style="color: #64748b; font-size: 14px; margin-bottom: 4px;"><strong>Banque :</strong> ${companyInfo.bankName}</div>`;
              }
              paymentInfoHTML += `<div style="color: #64748b; font-size: 14px; margin-bottom: 4px;"><strong>IBAN :</strong> ${companyInfo.iban}</div>`;
              if (companyInfo.bic) {
                paymentInfoHTML += `<div style="color: #64748b; font-size: 14px;"><strong>BIC :</strong> ${companyInfo.bic}</div>`;
              }
              paymentInfoHTML += '</div>';
            }
            
            // Délai de paiement
            if (companyInfo.paymentTermDays) {
              paymentInfoHTML += `<div style="color: #475569; font-size: 14px; margin-bottom: 8px;">⏱️ <strong>Délai de paiement :</strong> ${companyInfo.paymentTermDays} jours</div>`;
            }
            
            // Pénalités
            if (companyInfo.latePenaltyText) {
              paymentInfoHTML += `<div style="color: #64748b; font-size: 13px; margin-top: 10px; line-height: 1.6;">⚠️ ${companyInfo.latePenaltyText}</div>`;
            }
            
            paymentInfoHTML += '</div>';
          }

          await addDoc(collection(db, 'mail'), {
              to: [client.email],
              bcc: [user.email], 
              message: {
                subject: emailData.subject || `${type === 'quote' ? 'Devis' : 'Facture'} ${docInfo.number} - ${companyInfo.name}`,
                html: `
                  <div style="font-family: 'Segoe UI', Arial, sans-serif; color: #333; line-height: 1.8; max-width: 600px; margin: 0 auto;">
                    <div style="padding: 30px 20px; background: linear-gradient(135deg, #667eea 0%, #764ba2 100%); border-radius: 12px 12px 0 0;">
                      <h2 style="margin: 0; color: white; font-size: 24px;">${companyInfo.name}</h2>
                      <p style="margin: 5px 0 0 0; color: rgba(255,255,255,0.9); font-size: 14px;">${type === 'quote' ? 'Nouveau devis' : 'Nouvelle facture'} - ${docInfo.number}</p>
                    </div>
                    
                    <div style="padding: 30px 20px; background: white;">
                      <p style="margin: 0 0 20px 0; color: #475569; font-size: 15px; line-height: 1.6;">
                        ${emailData.message.replace(/\n/g, '<br/>')}
                      </p>
                      
                      <div style="padding: 15px; background: #eff6ff; border-left: 4px solid #3b82f6; border-radius: 6px; margin: 20px 0;">
                        <p style="margin: 0; color: #1e40af; font-size: 14px;">
                          📎 Vous trouverez le document <strong>${docInfo.number}</strong> en pièce jointe au format PDF.
                        </p>
                      </div>
                      
                      ${signatureButtonHTML}
                      
                      ${paymentInfoHTML}
                      
                      <div style="margin-top: 30px; padding-top: 20px; border-top: 1px solid #e2e8f0;">
                        <p style="margin: 0 0 5px 0; color: #64748b; font-size: 14px;">Cordialement,</p>
                        <p style="margin: 0; color: #1e293b; font-size: 16px; font-weight: bold;">${companyInfo.name}</p>
                        ${companyInfo.phone ? `<p style="margin: 5px 0 0 0; color: #64748b; font-size: 13px;">📞 ${companyInfo.phone}</p>` : ''}
                        ${companyInfo.email ? `<p style="margin: 5px 0 0 0; color: #64748b; font-size: 13px;">✉️ ${companyInfo.email}</p>` : ''}
                      </div>
                    </div>
                    
                    <div style="padding: 15px 20px; background: #f8fafc; border-radius: 0 0 12px 12px; text-align: center;">
                      <p style="margin: 0; color: #94a3b8; font-size: 12px;">
                        Cet email a été envoyé depuis BatiFlow - Logiciel de gestion pour artisans
                      </p>
                    </div>
                  </div>
                `,
                attachments: attachments
              }
          });

          if (docInfo.id && docInfo.status === 'Brouillon') {
              const newStatus = type === 'quote' ? 'En attente' : 'Envoyée';
              const collectionName = type === 'quote' ? 'quotes' : 'invoices';
              setDocInfo(prev => ({...prev, status: newStatus}));
              const dataToSave = Object.fromEntries(
                  Object.entries({ ...docInfo, status: newStatus, clientName: client.name || '', clientId: client.id || null, total: totals.totalTTC, sections })
                      .filter(([_, value]) => value !== undefined)
              );
              await setDoc(doc(db, 'companies', activeCompanyId, collectionName, String(docInfo.id)), dataToSave);
          }
          
          if (logActivity) {
              logActivity('send_email', { 
                  documentType: type, 
                  documentNumber: docInfo.number,
                  clientEmail: client.email
              });
          }
          
          alert(`Email envoyé à ${client.email} avec le PDF joint !`);
      } catch (e) { console.error("Erreur mail:", e); alert("Erreur lors de l'envoi."); }
  };

  // Nettoyer les valeurs undefined pour Firestore
  const cleanDocData = (data) => {
    const cleaned = {};
    Object.keys(data).forEach(key => {
      if (data[key] !== undefined) {
        cleaned[key] = data[key];
      }
    });
    return cleaned;
  };

  const handleSave = async () => { 
    if (!user || !docInfo.id) return; 
    setIsSaving(true); 
    const totals = calculateTotals(sections, globalSettings); 
    const dataToSave = cleanDocData({ 
      ...docInfo, 
      clientName: client.name || '', 
      clientId: client.id || null,
      total: totals.totalTTC, 
      sections 
    });
    await setDoc(doc(db, 'companies', activeCompanyId, type === 'quote' ? 'quotes' : 'invoices', String(docInfo.id)), dataToSave); 
    
    if (logActivity) {
        const isNew = !docInfo.number; // Si pas de numéro, c'est un nouveau document
        logActivity(isNew ? (type === 'quote' ? 'create_quote' : 'create_invoice') : 'update_document', {
            documentType: type,
            documentNumber: docInfo.number,
            clientName: client.name,
            amount: totals.totalTTC
        });
    }
    
    setLastAutoSave(new Date()); 
    setIsSaving(false); 
    if (onSaveSuccess) onSaveSuccess(); 
  };
  const handleDelete = async () => { if (!docInfo.id) return; if (window.confirm("Supprimer définitivement ?")) { await deleteDoc(doc(db, 'companies', activeCompanyId, type === 'quote' ? 'quotes' : 'invoices', String(docInfo.id))); if (onBack) onBack(); } };
  const handleCreateClient = async () => { if (!newClientForm.name) return; const newId = Date.now(); await setDoc(doc(db, 'companies', activeCompanyId, 'clients', String(newId)), { id: newId, companyId: activeCompanyId, ...newClientForm }); setClient({ id: newId, ...newClientForm }); setShowNewClientModal(false); setShowClientSelector(false); setNewClientForm({ name: '', email: '', phone: '', phoneMobile: '', address: '', city: '', siret: '', contactName: '', contactFunction: '', notes: '' }); };
  const handleEditClient = async () => { if (!editClientForm.name || !client.id) return; await setDoc(doc(db, 'companies', activeCompanyId, 'clients', String(client.id)), { id: client.id, companyId: activeCompanyId, ...editClientForm }, { merge: true }); setClient({ id: client.id, ...editClientForm }); setShowEditClientModal(false); };
  const openEditClient = () => { if (!client.id) return; setEditClientForm({ name: client.name || '', email: client.email || '', phone: client.phone || '', phoneMobile: client.phoneMobile || '', address: client.address || '', city: client.city || '', siret: client.siret || '', contactName: client.contactName || '', contactFunction: client.contactFunction || '', notes: client.notes || '' }); setShowEditClientModal(true); };
  const handleAddFromLibrary = (item) => { if (targetSectionIndex === null) return; const newSections = [...sections]; newSections[targetSectionIndex].items.push({ id: Date.now(), desc: item.desc, qty: 1, unit: item.unit || 'u', price: item.price || 0, nature: item.nature || 'labor', vat: item.vat !== undefined ? item.vat : companyInfo.defaultVat }); setSections(newSections); setShowLibraryModal(false); setLibrarySearch(''); };
  const handleAddManualItem = (sIdx) => { const newSections = [...sections]; newSections[sIdx].items.push({ id: Date.now(), desc: "Nouvel élément", qty: 1, unit: 'u', price: 0, nature: 'labor', vat: companyInfo.defaultVat }); setSections(newSections); };
  const handleDeleteItem = (sIdx, iIdx) => { const newSections = [...sections]; newSections[sIdx].items.splice(iIdx, 1); setSections(newSections); };
  const handleDeleteSection = (sIdx) => { if (window.confirm("Supprimer cette section ?")) { const newSections = [...sections]; newSections.splice(sIdx, 1); setSections(newSections); } };
  
  const onSectionDragStart = (e, index) => { setDraggedSectionIndex(index); e.dataTransfer.effectAllowed = 'move'; };
  const onSectionDragOver = (e, index) => { e.preventDefault(); if (draggedSectionIndex === null || draggedSectionIndex === index) return; const newSections = [...sections]; const [movedSection] = newSections.splice(draggedSectionIndex, 1); newSections.splice(index, 0, movedSection); setSections(newSections); setDraggedSectionIndex(index); };
  const onSectionDragEnd = () => { setDraggedSectionIndex(null); };
  const onItemDragStart = (e, sIdx, iIdx) => { e.stopPropagation(); setDraggedItem({ s: sIdx, i: iIdx }); e.dataTransfer.effectAllowed = 'move'; };
  const onItemDragOver = (e, targetS, targetI) => { e.preventDefault(); e.stopPropagation(); if (!draggedItem) return; if (draggedItem.s === targetS && draggedItem.i === targetI) return; const newSections = sections.map(sec => ({...sec, items: [...sec.items]})); const sourceList = newSections[draggedItem.s].items; const targetList = newSections[targetS].items; const [movedItem] = sourceList.splice(draggedItem.i, 1); targetList.splice(targetI, 0, movedItem); setSections(newSections); setDraggedItem({ s: targetS, i: targetI }); };
  const onItemDragEnd = () => { setDraggedItem(null); };
  const toggleNature = (sIdx, iIdx, nature) => { const newSections = [...sections]; newSections[sIdx].items[iIdx].nature = nature; setSections(newSections); };
  
  const filteredLibrary = libraryItems.filter(item => item.desc.toLowerCase().includes(librarySearch.toLowerCase()));
  const UNIT_OPTIONS = [
    { label: "QUANTITÉS", options: ["u", "pièce", "pc", "ens", "lot", "forfait"] },
    { label: "TEMPS", options: ["h", "heure", "j", "jour", "sem", "mois"] },
    { label: "LONGUEURS", options: ["m", "cm", "mm", "km", "ml"] },
    { label: "SURFACES", options: ["m²", "m2"] },
    { label: "VOLUMES", options: ["m³", "m3", "l", "litre", "cl"] },
    { label: "POIDS", options: ["kg", "g", "t", "tonne"] },
    { label: "CONDITIONNEMENTS", options: ["sac", "carton", "palette", "rouleau", "bidon"] }
  ];
  const statusOptions = type === 'quote' ? ['Brouillon', 'En attente', 'Signé', 'Refusé'] : ['Brouillon', 'En attente', 'Payée', 'En retard'];
  const getStatusColor = (status) => { switch(status) { case 'Signé': case 'Payée': return 'bg-green-100 text-green-700 border-green-200'; case 'En attente': return 'bg-orange-100 text-orange-700 border-orange-200'; case 'Refusé': case 'En retard': return 'bg-red-100 text-red-700 border-red-200'; default: return 'bg-slate-100 text-slate-500 border-slate-200'; } };
  
  const convertToInvoice = async (invoiceType) => { 
    if (!docInfo.id) return; 
    
    // Sauvegarder le devis actuel avant de créer la facture
    await handleSave();
    
    let invoiceSections = JSON.parse(JSON.stringify(sections)); 
    let invoiceTotal = totals.totalTTC; 
    
    if (invoiceType === 'deposit') { 
      // Facture d'acompte : calculer le montant selon le %
      const depositPercent = globalSettings.downPaymentValue || 30;
      invoiceTotal = totals.totalTTC * (depositPercent / 100); 
      const priceHT = invoiceTotal / (1 + (companyInfo.defaultVat / 100));
      invoiceSections = [{ 
        id: Date.now(), 
        title: "Acompte", 
        items: [{ 
          id: Date.now(), 
          desc: `Acompte ${depositPercent}% sur devis ${docInfo.number}`, 
          qty: 1, 
          unit: 'u', 
          price: priceHT, 
          vat: companyInfo.defaultVat 
        }] 
      }]; 
    } 
    
    const newInvoiceId = Date.now(); 
    const invoiceData = { 
      id: newInvoiceId, 
      type: 'invoice', 
      number: generateNumber('invoice'), 
      clientName: client.name || '', 
      clientEmail: client.email || '',
      clientPhone: client.phone || '',
      clientAddress: client.address || '',
      clientCity: client.city || '',
      clientPostalCode: client.postalCode || '',
      date: new Date().toLocaleDateString('fr-FR'), 
      status: 'Brouillon', 
      sections: invoiceSections, 
      total: invoiceTotal, 
      sourceQuote: docInfo.number, 
      projectAddress: docInfo.projectAddress,
      companyId: activeCompanyId,
      createdAt: new Date()
    }; 
    
    console.log('[Editor] Creating invoice:', invoiceType, invoiceData);
    await setDoc(doc(db, 'companies', activeCompanyId, 'invoices', String(newInvoiceId)), invoiceData); 
    
    // Rediriger vers l'éditeur de facture
    if(onRedirect) {
      onRedirect(newInvoiceId, 'invoice');
    }
  };

  return (
    <div className="max-w-5xl mx-auto p-2 md:p-6 animate-page pb-32">
      
      {/* Bandeau info : Devis depuis Prospect */}
      {docInfo.fromProspect && (
        <div className="bg-gradient-to-r from-blue-50 to-indigo-50 border-2 border-blue-200 rounded-2xl p-4 mb-6 flex items-start gap-4 animate-in fade-in">
          <div className="w-10 h-10 bg-blue-500 rounded-full flex items-center justify-center flex-shrink-0">
            <UserPlus size={20} className="text-white"/>
          </div>
          <div className="flex-1">
            <h3 className="font-bold text-blue-900 mb-1">📋 Devis préparé depuis un prospect</h3>
            <p className="text-sm text-blue-700 mb-2">
              Les informations client et la liste des travaux ont été pré-remplies. 
              <strong> Ajoutez maintenant les prix et détails</strong> avant d'envoyer le devis.
            </p>
            <div className="flex gap-2 text-xs">
              <span className="bg-blue-100 text-blue-700 px-2 py-1 rounded-full font-medium">
                ℹ️ Pensez à demander vos prix fournisseurs
              </span>
              <span className="bg-green-100 text-green-700 px-2 py-1 rounded-full font-medium">
                ✅ Client : {client.name || 'Non défini'}
              </span>
            </div>
          </div>
        </div>
      )}

      {/* MODALE NEW CLIENT */}
      {showNewClientModal && (
          <div className="fixed inset-0 bg-slate-900/50 backdrop-blur-sm z-50 flex items-center justify-center p-4 animate-in zoom-in-95" onClick={() => setShowNewClientModal(false)}>
              <div className="bg-white p-6 rounded-2xl shadow-2xl w-full max-w-2xl max-h-[90vh] overflow-y-auto" onClick={e => e.stopPropagation()}>
                  <h3 className="font-bold text-lg mb-4 flex items-center gap-2"><UserPlus size={20} className="text-blue-600"/> Nouveau Client</h3>
                  <div className="grid grid-cols-2 gap-3">
                      <div className="col-span-2"><input className="w-full border rounded-lg p-2 text-sm" placeholder="Nom / Entreprise *" value={newClientForm.name} onChange={e => setNewClientForm({...newClientForm, name: e.target.value})} autoFocus /></div>
                      <input className="w-full border rounded-lg p-2 text-sm" placeholder="SIRET" value={newClientForm.siret} onChange={e => setNewClientForm({...newClientForm, siret: e.target.value})} />
                      <input className="w-full border rounded-lg p-2 text-sm" placeholder="Email" value={newClientForm.email} onChange={e => setNewClientForm({...newClientForm, email: e.target.value})} />
                      <input className="w-full border rounded-lg p-2 text-sm" placeholder="Tél. fixe" value={newClientForm.phone} onChange={e => setNewClientForm({...newClientForm, phone: e.target.value})} />
                      <input className="w-full border rounded-lg p-2 text-sm" placeholder="Tél. mobile" value={newClientForm.phoneMobile} onChange={e => setNewClientForm({...newClientForm, phoneMobile: e.target.value})} />
                      <input className="w-full border rounded-lg p-2 text-sm" placeholder="Nom du contact" value={newClientForm.contactName} onChange={e => setNewClientForm({...newClientForm, contactName: e.target.value})} />
                      <input className="w-full border rounded-lg p-2 text-sm" placeholder="Fonction du contact" value={newClientForm.contactFunction} onChange={e => setNewClientForm({...newClientForm, contactFunction: e.target.value})} />
                      <input className="w-full border rounded-lg p-2 text-sm" placeholder="Adresse" value={newClientForm.address} onChange={e => setNewClientForm({...newClientForm, address: e.target.value})} />
                      <input className="w-full border rounded-lg p-2 text-sm" placeholder="Ville" value={newClientForm.city} onChange={e => setNewClientForm({...newClientForm, city: e.target.value})} />
                      <div className="col-span-2"><textarea className="w-full border rounded-lg p-2 text-sm" rows="2" placeholder="Notes privées (non visibles sur les devis)" value={newClientForm.notes} onChange={e => setNewClientForm({...newClientForm, notes: e.target.value})} /></div>
                  </div>
                  <div className="flex justify-end gap-2 mt-4"><Button variant="secondary" onClick={() => setShowNewClientModal(false)}>Annuler</Button><Button variant="primary" onClick={handleCreateClient} disabled={!newClientForm.name}>Créer et Sélectionner</Button></div>
              </div>
          </div>
      )}

      {/* MODALE EDIT CLIENT */}
      {showEditClientModal && (
          <div className="fixed inset-0 bg-slate-900/50 backdrop-blur-sm z-50 flex items-center justify-center p-4 animate-in zoom-in-95" onClick={() => setShowEditClientModal(false)}>
              <div className="bg-white p-6 rounded-2xl shadow-2xl w-full max-w-2xl max-h-[90vh] overflow-y-auto" onClick={e => e.stopPropagation()}>
                  <h3 className="font-bold text-lg mb-4 flex items-center gap-2"><Edit3 size={20} className="text-green-600"/> Modifier le Client</h3>
                  <div className="grid grid-cols-2 gap-3">
                      <div className="col-span-2"><input className="w-full border rounded-lg p-2 text-sm" placeholder="Nom / Entreprise *" value={editClientForm.name} onChange={e => setEditClientForm({...editClientForm, name: e.target.value})} autoFocus /></div>
                      <input className="w-full border rounded-lg p-2 text-sm" placeholder="SIRET" value={editClientForm.siret} onChange={e => setEditClientForm({...editClientForm, siret: e.target.value})} />
                      <input className="w-full border rounded-lg p-2 text-sm" placeholder="Email" value={editClientForm.email} onChange={e => setEditClientForm({...editClientForm, email: e.target.value})} />
                      <input className="w-full border rounded-lg p-2 text-sm" placeholder="Tél. fixe" value={editClientForm.phone} onChange={e => setEditClientForm({...editClientForm, phone: e.target.value})} />
                      <input className="w-full border rounded-lg p-2 text-sm" placeholder="Tél. mobile" value={editClientForm.phoneMobile} onChange={e => setEditClientForm({...editClientForm, phoneMobile: e.target.value})} />
                      <input className="w-full border rounded-lg p-2 text-sm" placeholder="Nom du contact" value={editClientForm.contactName} onChange={e => setEditClientForm({...editClientForm, contactName: e.target.value})} />
                      <input className="w-full border rounded-lg p-2 text-sm" placeholder="Fonction du contact" value={editClientForm.contactFunction} onChange={e => setEditClientForm({...editClientForm, contactFunction: e.target.value})} />
                      <input className="w-full border rounded-lg p-2 text-sm" placeholder="Adresse" value={editClientForm.address} onChange={e => setEditClientForm({...editClientForm, address: e.target.value})} />
                      <input className="w-full border rounded-lg p-2 text-sm" placeholder="Ville" value={editClientForm.city} onChange={e => setEditClientForm({...editClientForm, city: e.target.value})} />
                      <div className="col-span-2"><textarea className="w-full border rounded-lg p-2 text-sm" rows="2" placeholder="Notes privées (non visibles sur les devis)" value={editClientForm.notes} onChange={e => setEditClientForm({...editClientForm, notes: e.target.value})} /></div>
                  </div>
                  <div className="flex justify-end gap-2 mt-4"><Button variant="secondary" onClick={() => setShowEditClientModal(false)}>Annuler</Button><Button variant="primary" onClick={handleEditClient} disabled={!editClientForm.name}>Enregistrer</Button></div>
              </div>
          </div>
      )}

      {/* MODALE BIBLIOTHEQUE */}
      {showLibraryModal && (
          <div className="fixed inset-0 bg-slate-900/50 backdrop-blur-sm z-50 flex items-center justify-center p-4 animate-in fade-in zoom-in-95" onClick={() => setShowLibraryModal(false)}>
              <div className="bg-white rounded-2xl shadow-2xl w-full max-w-2xl overflow-hidden flex flex-col max-h-[80vh]" onClick={e => e.stopPropagation()}>
                  <div className="p-4 border-b border-slate-100 flex justify-between items-center bg-slate-50"><h3 className="font-bold text-slate-800 flex items-center gap-2"><Library size={20} className="text-indigo-500"/> Bibliothèque de Prix</h3><button onClick={() => setShowLibraryModal(false)}><X size={20} className="text-slate-400 hover:text-slate-600"/></button></div>
                  <div className="p-4 border-b border-slate-100"><div className="relative"><Search className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400" size={16}/><input className="w-full pl-10 pr-4 py-2 border border-slate-200 rounded-xl text-sm outline-none focus:ring-2 focus:ring-indigo-500" placeholder="Rechercher un article..." value={librarySearch} onChange={e => setLibrarySearch(e.target.value)} autoFocus /></div></div>
                  <div className="flex-1 overflow-y-auto p-2">
                      {filteredLibrary.length === 0 ? <div className="text-center p-8 text-slate-400 text-sm">Aucun article trouvé.</div> : <div className="grid gap-2">{filteredLibrary.map(item => (<div key={item.id} onClick={() => handleAddFromLibrary(item)} className="flex items-center justify-between p-3 hover:bg-indigo-50 rounded-xl cursor-pointer border border-transparent hover:border-indigo-100 transition group"><div className="flex items-center gap-3"><div className={`w-8 h-8 rounded-lg flex items-center justify-center border ${item.nature === 'labor' ? 'bg-blue-50 border-blue-100 text-blue-600' : 'bg-amber-50 border-amber-100 text-amber-600'}`}>{item.nature === 'labor' ? <HardHat size={14}/> : <Hammer size={14}/>}</div><div><div className="font-bold text-sm text-slate-700">{item.desc}</div><div className="text-xs text-slate-400">{item.unit}</div></div></div><div className="font-mono font-bold text-indigo-600">{formatCurrency(item.price)}</div></div>))}</div>}
                  </div>
              </div>
          </div>
      )}

      {/* Barre d'outils */}
      <div className="flex flex-col gap-4 mb-6 print:hidden">
        <div className="flex items-center gap-4">
            <button onClick={onBack} className="flex items-center gap-2 text-slate-500 hover:text-slate-800 font-medium transition hover:bg-slate-100 px-3 py-2 rounded-lg w-fit"><ArrowLeft size={18}/> Retour</button>
            <div className="text-xs text-slate-400 flex items-center gap-1.5 bg-white px-3 py-1.5 rounded-full border border-slate-100 shadow-sm">{isSaving ? <><Loader2 size={12} className="animate-spin text-blue-500"/> Sauvegarde...</> : lastAutoSave ? <><Cloud size={12} className="text-green-500"/> Sauvegardé à {lastAutoSave.toLocaleTimeString([], {hour: '2-digit', minute:'2-digit'})}</> : <><Cloud size={12}/> Prêt</>}</div>
        </div>
        <div className="flex flex-wrap gap-2 items-center">
          {docId && (
            <Button 
              variant={isReadOnly ? "primary" : "secondary"} 
              onClick={() => setIsReadOnly(!isReadOnly)} 
              icon={Edit3} 
              className="whitespace-nowrap"
            >
              {isReadOnly ? 'Modifier' : 'Mode Lecture'}
            </Button>
          )}
          {type === 'quote' && docInfo.id && !isReadOnly && (<><Button variant="secondary" onClick={() => convertToInvoice('deposit')} icon={Percent} className="whitespace-nowrap">Facture Acompte</Button><Button variant="blue" onClick={() => convertToInvoice('final')} icon={FileCheck} className="whitespace-nowrap">Facture Solde</Button><div className="hidden md:block w-px h-8 bg-slate-200 mx-1"></div></>)}
          <Button variant="secondary" onClick={() => window.print()} icon={Printer} className="whitespace-nowrap">Imprimer</Button>
          {!isReadOnly && <Button variant="primary" onClick={handleSave} icon={Save} className="whitespace-nowrap">Sauvegarder</Button>}
          <Button variant="secondary" onClick={() => setShowEmailModal(true)} icon={Mail} className="whitespace-nowrap">Envoyer</Button>
          {docInfo.id && client.email && !docInfo.signed && (
            <Button variant="purple" onClick={handleRequestSignature} icon={PenTool} className="whitespace-nowrap">
              Demander signature
            </Button>
          )}
          {docInfo.signed && (
            <div className="flex items-center gap-2 px-4 py-2 bg-green-50 border border-green-200 rounded-lg">
              <CheckCircle2 size={18} className="text-green-600"/>
              <span className="text-sm font-medium text-green-700">Signé par {docInfo.signedBy || 'le client'}</span>
            </div>
          )}
          {!isReadOnly && <Button variant="danger" onClick={handleDelete} icon={Trash2} className="whitespace-nowrap">Supprimer</Button>}
        </div>
      </div>

      <div className="bg-white shadow-xl md:shadow-2xl md:rounded-3xl min-h-[600px] md:min-h-[900px] p-4 md:p-12 relative print:shadow-none print:p-0">
        <div className="flex flex-col md:flex-row justify-between mb-8 md:mb-16 border-b border-slate-100 pb-8 gap-6">
          <div className="flex items-start gap-4">
            {companyInfo.logoUrl && <img src={companyInfo.logoUrl} alt="Logo" className="w-20 h-20 object-contain rounded-lg border border-slate-100" />}
            <div>
              <h1 className="text-3xl md:text-4xl font-extrabold text-slate-900 tracking-tighter mb-2">{companyInfo.name}</h1>
              <div className="text-slate-500 text-sm font-medium space-y-1">
                <p>{companyInfo.address}</p><p>{companyInfo.city}</p><p className="text-blue-600">{companyInfo.email}</p><p>{companyInfo.phone}</p>
              </div>
            </div>
          </div>
          <div className="w-full md:w-72">
            <div className="bg-slate-50 p-4 md:p-5 rounded-xl md:rounded-2xl border border-slate-100 relative">
              <div className="text-[10px] font-bold text-slate-400 uppercase tracking-widest mb-2">Client</div>
              <div className="font-bold text-slate-800 text-lg mb-1">{client.name || 'Sélectionner...'}</div>
              <div className="text-sm text-slate-500">{client.address} {client.city}</div>
              {!isReadOnly && (
                <div className="absolute top-3 right-3 flex gap-2">
                  {client.id && (<button onClick={(e) => { e.stopPropagation(); openEditClient(); }} className="text-green-600 hover:bg-green-50 p-1.5 rounded transition"><Edit3 size={14}/></button>)}
                  <button onClick={() => setShowClientSelector(!showClientSelector)} className="text-blue-400 hover:bg-blue-50 p-1.5 rounded transition"><ChevronDown size={14}/></button>
                </div>
              )}
              {showClientSelector && !isReadOnly && (<div className="absolute top-full right-0 mt-2 w-full bg-white border border-slate-200 rounded-xl shadow-xl z-10 max-h-60 overflow-y-auto"><div className="p-3 hover:bg-green-50 text-green-700 font-bold text-sm border-b border-slate-50 cursor-pointer flex items-center gap-2" onClick={(e) => { e.stopPropagation(); setShowNewClientModal(true); }}><Plus size={16}/> Nouveau Client</div>{clientsList.map(c => (<div key={c.id} className="p-3 hover:bg-slate-50 text-left border-b border-slate-50 last:border-0" onClick={(e) => { e.stopPropagation(); setClient(c); setShowClientSelector(false); }}><div className="font-bold text-slate-800 text-sm">{c.name}</div></div>))}</div>)}
            </div>
          </div>
        </div>

        <div className="flex flex-col md:flex-row justify-between items-start md:items-end mb-8 md:mb-12 gap-4">
          {docInfo.projectAddress && (
            <div className="w-full md:w-1/2 print:block">
              {isEditingAddress ? (
                <div className="flex gap-2 items-start">
                  <textarea 
                    className="w-full bg-slate-50 border border-blue-200 rounded-xl p-3 text-sm focus:ring-2 focus:ring-blue-500 outline-none" 
                    rows="3" 
                    value={docInfo.projectAddress} 
                    onChange={e => setDocInfo({...docInfo, projectAddress: e.target.value})}
                  />
                  <button onClick={() => setIsEditingAddress(false)} className="bg-green-500 text-white p-2 rounded-lg">
                    <Check size={16}/>
                  </button>
                </div>
              ) : (
                <div onClick={() => !isReadOnly && setIsEditingAddress(true)} className="group cursor-pointer">
                  <div className="text-[10px] font-bold text-slate-400 uppercase tracking-widest mb-2 flex items-center gap-2 print:hidden">
                    Lieu des travaux {!isReadOnly && <Edit3 size={12}/>}
                  </div>
                  <div className="text-sm text-slate-700 font-medium bg-slate-50 p-3 rounded-xl border border-transparent hover:border-slate-200 transition print:bg-transparent print:border-0 print:p-0">
                    <span className="print:hidden">📍 </span>{docInfo.projectAddress}
                  </div>
                </div>
              )}
            </div>
          )}
          {!docInfo.projectAddress && !isReadOnly && (
            <div className="w-full md:w-1/2 print:hidden">
              {isEditingAddress ? (
                <div className="flex gap-2 items-start">
                  <textarea 
                    className="w-full bg-slate-50 border border-blue-200 rounded-xl p-3 text-sm focus:ring-2 focus:ring-blue-500 outline-none" 
                    rows="3" 
                    value={docInfo.projectAddress} 
                    onChange={e => setDocInfo({...docInfo, projectAddress: e.target.value})}
                    placeholder="Entrez l'adresse du chantier..."
                  />
                  <button onClick={() => setIsEditingAddress(false)} className="bg-green-500 text-white p-2 rounded-lg">
                    <Check size={16}/>
                  </button>
                </div>
              ) : (
                <div onClick={() => setIsEditingAddress(true)} className="group cursor-pointer">
                  <div className="text-[10px] font-bold text-slate-400 uppercase tracking-widest mb-2 flex items-center gap-2">
                    Lieu des travaux <Edit3 size={12}/>
                  </div>
                  <div className="text-sm text-slate-500 italic bg-slate-50 p-3 rounded-xl border border-dashed border-slate-200 hover:border-slate-300 transition">
                    Cliquez pour ajouter l'adresse du chantier
                  </div>
                </div>
              )}
            </div>
          )}
          
          <div className="text-left md:text-right w-full md:w-auto">
            <div className="text-2xl md:text-4xl font-bold text-slate-900 mb-1">{docInfo.number}</div><div className="text-slate-400 font-medium text-sm mb-3">Émis le {docInfo.date}</div>
            <div className="relative inline-block"><select value={docInfo.status} onChange={(e) => setDocInfo({...docInfo, status: e.target.value})} className={`appearance-none pl-4 pr-8 py-2 rounded-lg text-xs font-bold uppercase tracking-wider border cursor-pointer transition shadow-sm outline-none ${getStatusColor(docInfo.status)}`}>{statusOptions.map(s => <option key={s} value={s} className="bg-white text-slate-800">{s}</option>)}</select><div className="pointer-events-none absolute inset-y-0 right-0 flex items-center px-2 text-current opacity-50"><ChevronDown size={14} /></div></div>
          </div>
        </div>

        {/* --- SECTIONS DU DEVIS --- */}
        <div className="mb-12">
          <div className="hidden md:grid grid-cols-12 bg-slate-900 text-white text-[10px] font-bold uppercase tracking-widest py-4 px-6 rounded-t-2xl"><div className="col-span-5">Désignation</div><div className="col-span-2 text-center">Qté / Unité</div><div className="col-span-2 text-right">PU HT</div><div className="col-span-1 text-right">Total HT</div><div className="col-span-2 text-right">Total TTC</div></div>
          <div className="border-t-0 md:border border-slate-200 rounded-b-2xl">
            {sections.map((section, sIdx) => (
              <div key={section.id || sIdx} className="border-b border-slate-200 last:border-0">
                <div className="bg-slate-100 md:bg-slate-50 px-4 md:px-6 py-3 flex justify-between items-center cursor-move group">
                  <div className="flex items-center gap-2 flex-1">
                    {!isReadOnly && <GripVertical size={14} className="text-slate-300 group-hover:text-slate-500"/>}
                    <input 
                      className="bg-transparent outline-none w-full font-bold text-slate-700 uppercase tracking-wide text-xs" 
                      value={section.title} 
                      onChange={e => {const ns = [...sections]; ns[sIdx].title = e.target.value; setSections(ns);}} 
                      placeholder="TITRE DE LA SECTION"
                      disabled={isReadOnly}
                    />
                  </div>
                  {!isReadOnly && <button onClick={() => handleDeleteSection(sIdx)} className="text-slate-400 hover:text-red-500 transition"><Trash2 size={14}/></button>}
                </div>
                
                {section.items.map((item, iIdx) => {
                  const totalHT = item.qty * item.price;
                  const totalTTC = totalHT * (1 + companyInfo.defaultVat / 100);
                  
                  // Filtrer les unités selon le type
                  // labor = temps uniquement, material = tout sauf temps
                  const filteredUnits = item.nature === 'labor' 
                    ? UNIT_OPTIONS.filter(group => group.label === 'TEMPS')
                    : item.nature === 'material'
                    ? UNIT_OPTIONS.filter(group => group.label !== 'TEMPS')
                    : UNIT_OPTIONS;
                  
                  // Cacher les lignes marquées comme notes
                  if (item.isNote) {
                    return (
                      <div key={item.id || iIdx} className="px-4 md:px-6 py-3 bg-blue-50 border-b border-blue-100">
                        <div className="text-xs text-slate-600 whitespace-pre-wrap font-mono">{item.desc}</div>
                      </div>
                    );
                  }
                  
                  return (
                  <div key={item.id || iIdx} className="flex flex-col md:grid md:grid-cols-12 px-4 md:px-6 py-4 text-sm items-start bg-white border-b border-slate-100 last:border-0 hover:bg-blue-50/20 transition group print:py-3">
                    <div className="w-full md:col-span-5 md:pr-4 mb-3 md:mb-0 relative print:col-span-5">
                      {isReadOnly ? (
                        <div className="w-full px-3 py-2 font-medium text-slate-700 whitespace-pre-wrap leading-relaxed print:text-sm print:leading-normal">
                          {item.desc}
                        </div>
                      ) : (
                        <textarea 
                          rows={3} 
                          className="w-full bg-slate-50 border border-slate-200 rounded-lg px-3 py-2 outline-none resize-y overflow-auto font-medium text-slate-700 focus:ring-2 focus:ring-blue-500 focus:border-blue-500 focus:bg-white transition" 
                          value={item.desc} 
                          onChange={e => {
                            const ns=[...sections];
                            ns[sIdx].items[iIdx].desc=e.target.value;
                            setSections(ns);
                            setTimeout(() => {
                              e.target.style.height='auto';
                              e.target.style.height=e.target.scrollHeight+'px';
                            }, 0);
                          }}
                          onFocus={e => {
                            e.target.style.height='auto';
                            e.target.style.height=e.target.scrollHeight+'px';
                          }}
                          style={{ minHeight: '80px', lineHeight: '1.6' }}
                        />
                      )}
                      {!isReadOnly && <div className="flex gap-2 mt-1 opacity-0 group-hover:opacity-100 transition-opacity"><button onClick={() => toggleNature(sIdx, iIdx, 'labor')} className={`text-[9px] px-1.5 py-0.5 rounded uppercase font-bold border ${item.nature === 'labor' ? 'bg-blue-100 text-blue-700 border-blue-200' : 'bg-white text-slate-400'}`}>Main d'oeuvre</button><button onClick={() => toggleNature(sIdx, iIdx, 'material')} className={`text-[9px] px-1.5 py-0.5 rounded uppercase font-bold border ${item.nature === 'material' ? 'bg-amber-100 text-amber-700 border-amber-200' : 'bg-white text-slate-400'}`}>Matériel</button></div>}
                    </div>
                    <div className="flex flex-wrap md:contents gap-2 w-full items-center mt-2 md:mt-0 print:contents">
                        <div className="flex items-center gap-2 md:col-span-2 md:justify-center print:col-span-2 print:justify-center">
                          <input className="w-14 text-center bg-slate-100 rounded-md py-1.5 outline-none font-medium text-slate-700 print:bg-white print:border print:border-slate-200 print:text-sm" value={item.qty} onChange={e => {const ns=[...sections];ns[sIdx].items[iIdx].qty=Number(e.target.value);setSections(ns)}} disabled={isReadOnly} />
                          <select 
                            className="w-20 text-center bg-white border border-slate-300 rounded-md py-1.5 text-xs font-medium text-slate-600 outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500 cursor-pointer hover:border-slate-400 transition print:text-xs print:py-1"
                            value={item.unit} 
                            onChange={e => {const ns=[...sections];ns[sIdx].items[iIdx].unit=e.target.value;setSections(ns)}}
                            disabled={isReadOnly}
                          >
                            <option value="">Unité</option>
                            {filteredUnits.map(group => (
                              <optgroup key={group.label} label={group.label}>
                                {group.options.map(unit => <option key={unit} value={unit}>{unit}</option>)}
                              </optgroup>
                            ))}
                          </select>
                        </div>
                        <div className="flex items-center gap-1 md:col-span-2 md:justify-end print:col-span-2 print:justify-end"><span className="md:hidden text-xs text-slate-400 print:hidden">Prix HT:</span><div className="flex items-center gap-1"><input className="w-16 md:w-20 text-right bg-transparent border-b border-transparent hover:border-slate-300 focus:border-blue-500 outline-none print:text-sm print:font-medium" value={item.price} onChange={e => {const ns=[...sections];ns[sIdx].items[iIdx].price=Number(e.target.value);setSections(ns)}} disabled={isReadOnly} /><span className="text-slate-600 print:text-sm">€</span></div></div>
                        <div className="md:col-span-1 text-right text-slate-600 font-medium text-xs print:col-span-1 print:text-sm">{formatCurrency(totalHT)}</div>
                        <div className="md:col-span-2 text-right font-bold text-slate-900 flex items-center justify-end gap-3 print:col-span-2 print:text-base">{formatCurrency(totalTTC)}{!isReadOnly && <button onClick={() => handleDeleteItem(sIdx, iIdx)} className="text-slate-300 hover:text-red-500 transition print:hidden"><Trash2 size={14}/></button>}</div>
                    </div>
                  </div>
                  );
                })}
                {!isReadOnly && <div className="px-6 py-3 bg-slate-50/50 flex items-center gap-4"><button onClick={() => handleAddManualItem(sIdx)} className="text-xs text-blue-600 font-bold flex items-center gap-1 hover:underline"><Plus size={14}/> Ajouter ligne</button><button onClick={() => { setTargetSectionIndex(sIdx); setShowLibraryModal(true); }} className="text-xs text-indigo-600 font-bold flex items-center gap-1 hover:underline"><Library size={14}/> Catalogue</button></div>}
              </div>
            ))}
          </div>
          {!isReadOnly && <div className="mt-4 flex justify-center"><button onClick={() => setSections([...sections, {id: Date.now(), title: "Nouvelle Section", items: []}])} className="text-xs bg-slate-800 text-white px-4 py-2.5 rounded-full shadow-lg flex items-center gap-2 font-bold uppercase"><LayoutGrid size={14}/> Ajouter une Section</button></div>}
        </div>

        <div className="flex flex-col md:flex-row justify-end border-t border-slate-100 pt-8 gap-8">
          <div className="w-full md:w-1/3 space-y-6">
            <div className="space-y-3 text-sm">
              <div className="flex justify-between text-slate-500"><span>Total HT</span> <span className="font-medium">{formatCurrency(totals.rawTotalHT)}</span></div>
              <div className="flex justify-between items-center text-slate-500 bg-slate-50 rounded-lg px-3 py-2 border border-slate-200">
                <span className="font-medium">TVA</span>
                <div className="flex items-center gap-2">
                  <select 
                    className="bg-white border border-slate-300 rounded-md px-2 py-1 text-xs font-bold text-slate-700 outline-none focus:ring-2 focus:ring-blue-500 cursor-pointer"
                    value={companyInfo.defaultVat} 
                    onChange={(e) => {
                      const newVat = Number(e.target.value);
                      setCompanyInfo({...companyInfo, defaultVat: newVat});
                      // Mettre à jour la TVA de tous les items existants
                      const updatedSections = sections.map(section => ({
                        ...section,
                        items: section.items.map(item => ({...item, vat: newVat}))
                      }));
                      setSections(updatedSections);
                    }}
                  >
                    <option value="0">0%</option>
                    <option value="5.5">5,5%</option>
                    <option value="10">10%</option>
                    <option value="20">20%</option>
                  </select>
                  <span className="font-medium">{formatCurrency(totals.totalTVA)}</span>
                </div>
              </div>
              <div className="flex justify-between text-2xl font-extrabold text-slate-900 pt-4 border-t border-slate-200"><span>Net à payer</span> <span>{formatCurrency(totals.totalTTC)}</span></div>
            </div>
            {type === 'quote' && (<div className="p-4 bg-blue-50 rounded-lg border border-blue-100 shadow-sm print:border-none print:shadow-none print:p-0 print:bg-transparent mt-4"><div className="flex justify-between items-center mb-2 text-blue-800 text-sm font-bold"><span className="flex items-center gap-2"><Percent size={16}/> Acompte requis</span><div className="flex items-center bg-white border border-blue-200 rounded-lg overflow-hidden w-32 print:border-none"><input type="number" className="w-full outline-none text-right font-bold text-blue-900 px-2 py-1.5 bg-transparent text-sm" value={globalSettings.downPaymentValue} onChange={(e) => setGlobalSettings({...globalSettings, downPaymentValue: Number(e.target.value)})} /><select className="bg-white text-blue-600 font-bold text-xs py-0.5 px-1 outline-none border-l border-blue-100 cursor-pointer appearance-none ml-1" value={globalSettings.downPaymentType} onChange={(e) => setGlobalSettings({...globalSettings, downPaymentType: e.target.value})}><option value="percent">%</option><option value="fixed">€</option></select></div></div><div className="flex justify-between items-center text-sm border-t border-blue-100 pt-2 mt-1"><span className="text-blue-600">Montant à régler :</span><span className="font-mono font-bold text-blue-900 text-lg">{formatCurrency(globalSettings.downPaymentType === 'percent' ? totals.totalTTC * (globalSettings.downPaymentValue / 100) : globalSettings.downPaymentValue)}</span></div></div>)}
            {docInfo.signed && docInfo.signatureUrl && (
              <div className="mt-8 pt-8 border-t border-slate-100">
                <div className="text-center p-6 bg-green-50 border border-green-100 rounded-xl relative overflow-hidden">
                  <div className="absolute top-0 right-0 p-2">
                    <CheckCircle2 className="text-green-200" size={40}/>
                  </div>
                  <div className="text-[10px] font-bold text-green-700 uppercase mb-3 tracking-widest">
                    Bon pour accord - Signé par le client
                  </div>
                  <img src={docInfo.signatureUrl} className="h-20 mx-auto opacity-90 mix-blend-multiply" alt="Signature" />
                  <div className="text-[10px] text-green-600 mt-3 font-medium">
                    Signé par {docInfo.signedBy || 'le client'} le {docInfo.signedAt ? new Date(docInfo.signedAt.toDate ? docInfo.signedAt.toDate() : docInfo.signedAt).toLocaleDateString('fr-FR') : 'N/A'}
                  </div>
                  {docInfo.signedEmail && (
                    <div className="text-[9px] text-green-500 mt-1">
                      Email : {docInfo.signedEmail}
                    </div>
                  )}
                </div>
              </div>
            )}
          </div>
        </div>

        {/* Section conditions de paiement et CGV - Visible uniquement à l'impression */}
        <div className="hidden print:block mt-12 page-break-before">
          {/* Coordonnées bancaires */}
          {companyInfo.iban && (
            <div className="mb-8">
              <div className="bg-slate-100 px-4 py-2 mb-4">
                <h3 className="font-bold text-slate-800 text-sm uppercase">💳 Conditions de paiement</h3>
              </div>
              <div className="px-4">
                <div className="mb-4">
                  <p className="font-bold text-slate-700 text-sm mb-2">Coordonnées bancaires :</p>
                  {companyInfo.bankName && (
                    <p className="text-slate-600 text-sm mb-1">Banque : {companyInfo.bankName}</p>
                  )}
                  <p className="text-slate-600 text-sm mb-1">IBAN : {companyInfo.iban}</p>
                  {companyInfo.bic && (
                    <p className="text-slate-600 text-sm">BIC : {companyInfo.bic}</p>
                  )}
                </div>

                {companyInfo.paymentTermDays && (
                  <p className="text-slate-600 text-sm mb-2">
                    ⏱️ <strong>Délai de paiement :</strong> {companyInfo.paymentTermDays} jours
                  </p>
                )}

                {companyInfo.latePenaltyText && (
                  <p className="text-slate-600 text-xs leading-relaxed mt-3">
                    ⚠️ {companyInfo.latePenaltyText}
                  </p>
                )}
              </div>
            </div>
          )}

          {/* Conditions Générales de Vente */}
          {companyInfo.cgv && companyInfo.cgv.trim() && (
            <div className="page-break-before">
              <div className="bg-slate-800 px-4 py-3 mb-4">
                <h2 className="font-bold text-white text-base uppercase">Conditions Générales de Vente</h2>
              </div>
              <div className="px-4 text-slate-700 text-xs leading-relaxed whitespace-pre-wrap">
                {companyInfo.cgv}
              </div>
            </div>
          )}

          {/* Footer légal */}
          {companyInfo.legalFooter && (
            <div className="mt-8 pt-4 border-t border-slate-200">
              <p className="text-slate-500 text-xs text-center leading-relaxed">
                {companyInfo.legalFooter}
              </p>
            </div>
          )}
        </div>
      </div>
      {showEmailModal && <EmailModal client={client} docNumber={docInfo.number} onClose={() => setShowEmailModal(false)} onSend={handleEmailSent} />}
    </div>
  );
};

export default Editor;